<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from "vue"
import { useFormContext } from "../composables/useFormContext"

const api = useFormContext()
const activeId = ref<string | null>(null)
let observer: IntersectionObserver | null = null
const ratioMap = new Map<Element, number>()

function clickSection(id: string) {
  const section = api.sections.value.find((s) => s.id === id)
  section?.el.value?.scrollIntoView({ behavior: "smooth", block: "start" })
  activeId.value = id
}

function rebuildObserver() {
  observer?.disconnect()
  ratioMap.clear()
  observer = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        ratioMap.set(e.target, e.isIntersecting ? e.intersectionRatio : 0)
      }
      let best: { id: string; ratio: number } | null = null
      for (const s of api.sections.value) {
        if (!s.el.value) continue
        const ratio = ratioMap.get(s.el.value) ?? 0
        if (!best || ratio > best.ratio) best = { id: s.id, ratio }
      }
      if (best && best.ratio > 0) activeId.value = best.id
    },
    { rootMargin: "-100px 0px -50% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
  )
  for (const s of api.sections.value) {
    if (s.el.value) observer.observe(s.el.value)
  }
}

watch(() => api.sections.value.length, rebuildObserver, { immediate: true, flush: "post" })

onBeforeUnmount(() => observer?.disconnect())
</script>

<template>
  <nav v-if="api.sections.value.length > 0" class="sticky top-20">
    <ul class="menu menu-vertical bg-base-100 rounded-box shadow-sm w-full">
      <li v-for="section in api.sections.value" :key="section.id">
        <a
          :class="{ 'menu-active': activeId === section.id }"
          @click.prevent="clickSection(section.id)"
        >
          <span>{{ section.title }}</span>
          <span v-if="section.hasError.value" class="text-error ml-auto" aria-label="Fehler">●</span>
        </a>
      </li>
    </ul>
  </nav>
</template>
